// src/features/system/developer/components/video-gallery/resourceHints.ts
// ────────────────────────────────────────────────────────────────────────────
// HTTP/2 + HTTP/3 resource-hint plumbing for the video feed.
//
// What this does:
//   • Injects `<link rel="preload" as="fetch" href={url}
//     fetchpriority="high" crossorigin="anonymous">` into <head> for the
//     next slide's signed playback URL. The browser opens / re-uses an
//     HTTPS connection to GCS *immediately*, hot-starts TLS, and raises
//     the priority of the subsequent Range request. On Chromium / Edge
//     this collapses the "tap → first frame" time by 100–300 ms because
//     the H/2 prioritiser places the active slide ahead of speculative
//     fetches that would otherwise share its connection.
//
//   • Uses `as="fetch"` (not `as="video"`) because we only want the
//     priority + connect-warm benefits — we do NOT want the browser to
//     buffer the entire response into memory (which `as="video"` would
//     attempt on some engines). The Range-prewarm fetch reuses the warm
//     socket and brings in only the 2 MB head.
//
//   • Single-flight: at most one hint per URL is mounted, and they are
//     reference-counted so swiping forward then back doesn't double-add.
//
//   • Safe-by-default: no-op outside a DOM environment (SSR, tests).
//
// References:
//   • https://web.dev/articles/preload-critical-assets
//   • https://web.dev/articles/fetch-priority
//   • https://developers.google.com/web/updates/2021/07/preload-hints
// ────────────────────────────────────────────────────────────────────────────

interface HintRef {
  el:    HTMLLinkElement;
  count: number;
}

const active = new Map<string, HintRef>();

/** Add (or refcount up) a preload hint for the given playback URL. */
export function addVideoHint(url: string): void {
  if (!url) return;
  if (typeof document === 'undefined') return;

  const existing = active.get(url);
  if (existing) {
    existing.count += 1;
    return;
  }

  try {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as  = 'fetch';
    link.href = url;
    // crossOrigin must be set when the eventual fetch is CORS — GCS
    // signed URLs are same-origin from the browser's POV (no creds),
    // but the `<video>` element issues "anonymous" requests, so we
    // match the credentials mode here for cache hit consistency.
    link.crossOrigin = 'anonymous';
    // Chromium-only attribute; ignored elsewhere. Raises the hint above
    // the rest of the speculative pre-fetch crowd.
    (link as HTMLLinkElement & { fetchPriority?: string }).fetchPriority = 'high';
    document.head.appendChild(link);
    active.set(url, { el: link, count: 1 });
  } catch { /* DOM unavailable / blocked — silent */ }
}

/** Decrement refcount; remove the hint when it drops to zero. */
export function removeVideoHint(url: string): void {
  if (!url) return;
  const ref = active.get(url);
  if (!ref) return;
  ref.count -= 1;
  if (ref.count > 0) return;
  try { ref.el.remove(); } catch { /* ignore */ }
  active.delete(url);
}

/** Drop every hint we own — for unmount/teardown. */
export function clearAllVideoHints(): void {
  for (const ref of Array.from(active.values())) {
    try { ref.el.remove(); } catch { /* ignore */ }
  }
  active.clear();
}
